import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import styles from "./GooglePlacesInput.module.css"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { Dropdown, DropdownToggle } from "reactstrap"
import { DropdownItem, DropdownMenu } from "components/common/Dropdown"
import Input from "components/form/Input"
import usePredictions from "modules/google/hooks/usePredictions"
import DynamicTag from "components/DynamicTag"
import { useDispatch, useSelector } from "react-redux"
import { addRecentSearch, removeRecentSearch } from "store/location"
import { ClockCircleOutlined, CloseOutlined, EnvironmentOutlined, CompassOutlined } from "@ant-design/icons"
import { cleanTrips } from "store/trips"

// Google returns a `viewport` (LatLngBounds) sized to the place — a country/island
// spans a wide box, a street address a tiny one. Serialize it to the "lat, lng"
// corner strings the rest of the app uses so the map can frame the whole region
// instead of dropping onto the nominal center point at a fixed zoom.
const placeViewportBounds = (place) => {
  const viewport = place?.geometry?.viewport
  if (!viewport) return
  const ne = viewport.getNorthEast()
  const sw = viewport.getSouthWest()
  return {
    northeast_corner: `${ne.lat()}, ${ne.lng()}`,
    southwest_corner: `${sw.lat()}, ${sw.lng()}`
  }
}

const GooglePlacesInput = (
  {
    tag,
    onInput,
    onChange,
    dropdownClassName,
    wrapperProps = null,
    dropDownMenuClassName,
    dropDownMenuProps = {},
    resetIfChanged = false,
    useDefaultValue = false,
    innerRef,
    name,
    updateSearchFilters,
    initialCoordinates = null, // Initial lat/lng to restore on clear
    shouldClearTripsOnSelect = false, // Whether to clear trips when location is selected
    ...rest
  },
  ref
) => {
  const inputRef = useRef(null)
  const { t } = useTranslation()
  const [predictions, place, getPredictions, selectPlace] = usePredictions()
  const dispatch = useDispatch()
  const recentSearches = useSelector((state) => state.location.recentSearches || [])
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const dropdownRef = useRef(null)
  const initialDefaultValue = useRef(rest.defaultValue || "")
  const hasUserInteracted = useRef(false) // Track if user has searched
  const userFocusedInput = useRef(false) // Track if user has focused the input

  const coordinates = useMemo(() => {
    if (!place) return
    const location = place?.geometry?.location
    if (!location) return
    const lat = location.lat()
    const lng = location.lng()
    return { lat, lng }
  }, [place])

  const placeBounds = useMemo(() => placeViewportBounds(place), [place])

  const handleKeyPress = (event) => event.key === "Enter" && event.preventDefault()
  const changeHandler = (event) => {
    if (event.persist) event.persist()
    setInputValue(event.target.value)
    getPredictions(event.target.value)
  }

  const handleClear = () => {
    // Only clear trips if explicitly requested (e.g., when on trips page)
    if (shouldClearTripsOnSelect) {
      dispatch(cleanTrips())
    }
    hasUserInteracted.current = false // Reset interaction flag

    // The X always fully clears the location. (It previously restored `initialCoordinates`,
    // which made clearing a Bahamas search snap right back to the Bahamas.) Drop the location
    // and its viewport entirely — clearing the bounds (rather than widening them to the whole
    // world) lets the trips map fall back to its default view, which is framed on the USA.
    const resetFilters = {
      address: "",
      lat: "",
      lng: "",
      northeast_corner: "",
      southwest_corner: ""
    }

    updateSearchFilters(resetFilters)
    setInputValue("")
    if (inputRef.current) inputRef.current.value = ""
    getPredictions("")

    if (onChange !== undefined && typeof onChange === "function") {
      onChange({
        target: {
          name,
          value: "",
          place: null,
          coordinates: undefined
        }
      })
    }
  }

  const clickHandler = (prediction) => (event) => {
    if (event.persist) event.persist()
    hasUserInteracted.current = true // Mark that user has interacted

    // Only clear trips if explicitly requested (e.g., when on trips page)
    if (shouldClearTripsOnSelect) {
      dispatch(cleanTrips())
    }

    selectPlace(prediction.place_id, (selectedPlace) => {
      if (useDefaultValue) return

      const location = selectedPlace?.geometry?.location
      const coordinates = location ? { lat: location.lat(), lng: location.lng() } : undefined
      const bounds = placeViewportBounds(selectedPlace)

      // Update input value
      const newValue = resetIfChanged ? "" : selectedPlace.formatted_address
      if (inputRef.current) inputRef.current.value = newValue
      setInputValue(newValue)

      // Always call onChange if provided
      if (onChange !== undefined && typeof onChange === "function") {
        onChange({
          target: {
            name,
            value: selectedPlace.formatted_address,
            place: selectedPlace,
            coordinates,
            bounds
          }
        })
      }

      dispatch(addRecentSearch({ place_id: prediction.place_id, description: prediction.description }))
      setIsOpen(false)
      userFocusedInput.current = false // Reset focus flag after selection
    })
  }

  const recentClickHandler = (search) => (event) => {
    if (event.persist) event.persist()
    hasUserInteracted.current = true // Mark that user has interacted

    // Only clear trips if explicitly requested (e.g., when on trips page)
    if (shouldClearTripsOnSelect) {
      dispatch(cleanTrips())
    }

    selectPlace(search.place_id, (selectedPlace) => {
      if (useDefaultValue) return

      const location = selectedPlace?.geometry?.location
      const coordinates = location ? { lat: location.lat(), lng: location.lng() } : undefined
      const bounds = placeViewportBounds(selectedPlace)

      // Update input value
      const newValue = resetIfChanged ? "" : search.description
      if (inputRef.current) inputRef.current.value = newValue
      setInputValue(newValue)

      // Always call onChange if provided
      if (onChange !== undefined && typeof onChange === "function") {
        onChange({
          target: {
            name,
            value: selectedPlace.formatted_address,
            place: selectedPlace,
            coordinates,
            bounds
          }
        })
      }

      setIsOpen(false)
      userFocusedInput.current = false // Reset focus flag after selection
    })
  }

  const removeRecentHandler = (place_id) => (event) => {
    event.stopPropagation()
    dispatch(removeRecentSearch(place_id))
  }

  useEffect(() => {
    const newValue = rest.defaultValue || ""
    // Sync the input to an externally-driven address change (e.g. the map's "current
    // location" button reverse-geocodes a new address into the search params). Skip when
    // it already matches what's shown so the user's own typing/selection isn't clobbered.
    const shownValue = inputRef.current?.value ?? inputValue
    if (hasUserInteracted.current && newValue === shownValue) return
    if (useDefaultValue && inputRef.current) inputRef.current.value = newValue
    setInputValue(newValue)
    if (!initialDefaultValue.current && newValue) {
      initialDefaultValue.current = newValue
    }
  }, [rest.defaultValue]) //eslint-disable-line

  useEffect(() => {
    if (onInput !== undefined && typeof onInput === "function") onInput({ target: { name, value: inputRef.current.value, predictions } })
  }, [predictions, onInput]) //eslint-disable-line

  useEffect(() => {
    if (!coordinates) return
    if (onChange !== undefined && typeof onChange === "function")
      onChange({ target: { name, value: place.formatted_address, place, coordinates, bounds: placeBounds } })
  }, [coordinates?.lat, coordinates?.lng]) //eslint-disable-line

  useEffect(() => {
    if (ref) ref.current = inputRef.current
  }, [ref])

  useEffect(() => {
    // Only open dropdown if user has actually focused/clicked the input
    if (userFocusedInput.current && (!!predictions?.length || !!recentSearches.length)) {
      setIsOpen(true)
    }
  }, [predictions, recentSearches])

  // Click-away handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isOpen) {
        setIsOpen(false)
        userFocusedInput.current = false // Reset focus flag when clicking away
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const handleInputFocus = () => {
    userFocusedInput.current = true
    setIsOpen(true)
  }

  const handleInputClick = () => {
    userFocusedInput.current = true
    setIsOpen(true)
  }
  const hasRecent = (recentSearches?.length || 0) > 0
  const hasPredictions = (predictions?.length || 0) > 0
  const showMenu = hasRecent || hasPredictions
  return (
    <DynamicTag tag={wrapperProps ? "div" : React.Fragment} {...wrapperProps}>
      <div className={styles.inputWrapper}>
        <DynamicTag
          {...rest}
          tag={tag || Input}
          type="text"
          onKeyPress={handleKeyPress}
          onChange={changeHandler}
          ref={inputRef}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          value={inputValue}
        />
        {/* Hide clear icon when disabled so cursor pointer isn't shown */}
        {inputValue && !rest.disabled && <CloseOutlined className={styles.clearIcon} onClick={handleClear} aria-label="Clear" />}
      </div>
      {showMenu && (
        <Dropdown direction="down" isOpen={isOpen} toggle={() => setIsOpen((open) => !open)} innerRef={dropdownRef} ref={inputRef}>
          <DropdownToggle className="position-absolute p-0 border-0 opacity-0 pointer-events-none" />
          <DropdownMenu {...dropDownMenuProps} container="body">
            <div className={`vstack ${styles.menuMinWidth}`}>
              {recentSearches?.length > 0 && (
                <div className="dropdown-header fw-bold px-3 pt-2 pb-1">{t("googlePlacesInput.recent_searches")}</div>
              )}
              {recentSearches?.map((search) => (
                <DropdownItem
                  key={search.place_id}
                  className="d-flex align-items-center justify-content-between"
                  onClick={recentClickHandler(search)}
                >
                  <span>
                    <ClockCircleOutlined className={styles.predictionIcon} />
                    {search.description}
                  </span>
                  <CloseOutlined className={styles.recentCloseIcon} onClick={removeRecentHandler(search.place_id)} />
                </DropdownItem>
              ))}
              <div className="dropdown-divider my-1" />
              {predictions?.length > 0 && (
                <>
                  <div className="dropdown-header fw-bold px-3 pt-2 pb-1">{t("googlePlacesInput.recommendations")}</div>
                  <DropdownItem className="d-flex align-items-center text-muted" disabled>
                    <CompassOutlined className={styles.predictionIcon} />
                    <span className="ms-2">{t("googlePlacesInput.search_locations_near_you")}</span>
                  </DropdownItem>
                  {predictions.map((prediction) => (
                    <DropdownItem
                      className="d-flex align-items-center text-wrap"
                      onClick={clickHandler(prediction)}
                      key={prediction.place_id}
                    >
                      <EnvironmentOutlined className={styles.predictionIcon} />
                      {prediction.description}
                    </DropdownItem>
                  ))}
                </>
              )}
            </div>
          </DropdownMenu>
        </Dropdown>
      )}
    </DynamicTag>
  )
}

GooglePlacesInput.propTypes = {
  tag: PropTypes.elementType,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  dropdownClassName: PropTypes.string,
  wrapperProps: PropTypes.object,
  dropDownMenuClassName: PropTypes.string,
  dropDownMenuProps: PropTypes.object,
  resetIfChanged: PropTypes.bool,
  useDefaultValue: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  name: PropTypes.string,
  updateSearchFilters: PropTypes.func,
  initialCoordinates: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  shouldClearTripsOnSelect: PropTypes.bool
}
export default forwardRef(GooglePlacesInput)
